import { useEffect, useState } from "react";
import axios from "axios";
import { Mail, Save, Send, CheckCircle2, AlertCircle, Loader2 } from "lucide-react";
import { authHeader } from "@/hooks/useAdminAuth";
import API from "@/lib/api";
import { Field, TextInput, NumberInput, Select } from "@/components/admin/AdminFormModal";

const EMPTY = {
  host: "smtp.hostinger.com",
  port: 465,
  secure: true,
  username: "webmaster@ngxptechnologies.com",
  password: "",
  from_email: "webmaster@ngxptechnologies.com",
  from_name: "NGXP Technologies",
  to_email: "s.lohidhasan071297@gmail.com",
  enabled: true,
};

export default function AdminSmtp() {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [feedback, setFeedback] = useState(null); // { kind: 'success'|'error', message }
  const [testTo, setTestTo] = useState("");

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/smtp`, { headers: authHeader() })
      .then(({ data }) => setForm({ ...EMPTY, ...data, password: "" }))
      .catch((err) => setFeedback({ kind: "error", message: err.response?.data?.error || err.message }))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const save = async (e) => {
    e?.preventDefault?.();
    setSaving(true);
    setFeedback(null);
    try {
      const { data } = await axios.put(
        `${API}/smtp`,
        { ...form, port: Number(form.port) || 587 },
        { headers: authHeader() }
      );
      setForm({ ...EMPTY, ...data, password: "" });
      setFeedback({ kind: "success", message: "SMTP settings saved." });
    } catch (err) {
      setFeedback({ kind: "error", message: err.response?.data?.error || err.message });
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    setTesting(true);
    setFeedback(null);
    try {
      const { data } = await axios.post(
        `${API}/smtp/test`,
        { to: testTo || undefined },
        { headers: authHeader() }
      );
      const recip = Array.isArray(data.accepted) && data.accepted.length ? data.accepted.join(", ") : (testTo || form.to_email || form.from_email);
      setFeedback({ kind: "success", message: `Test email sent to ${recip}.` });
    } catch (err) {
      setFeedback({ kind: "error", message: err.response?.data?.error || err.message });
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-[#7B2CBF] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-3xl">
      <div className="mb-6 flex items-start gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#00A8E8] to-[#7B2CBF] flex items-center justify-center">
          <Mail className="w-5 h-5 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-white font-display">SMTP Settings</h1>
          <p className="text-gray-500 mt-1 text-sm">
            Configure outgoing email for contact-form notifications.
          </p>
        </div>
      </div>

      {feedback && (
        <div
          className={`mb-5 flex items-start gap-2 px-4 py-3 rounded-lg border text-sm ${
            feedback.kind === "success"
              ? "bg-green-400/10 border-green-400/30 text-green-300"
              : "bg-red-400/10 border-red-400/30 text-red-300"
          }`}
        >
          {feedback.kind === "success" ? (
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
          ) : (
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
          )}
          <span className="break-words">{feedback.message}</span>
        </div>
      )}

      <form onSubmit={save} className="bg-[#0D0E13] border border-white/5 rounded-2xl p-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div className="sm:col-span-2">
            <Field label="SMTP Host" required>
              <TextInput
                value={form.host}
                onChange={(e) => update("host", e.target.value)}
                placeholder="smtp.hostinger.com"
                required
              />
            </Field>
          </div>
          <Field label="Port" required>
            <NumberInput
              value={form.port}
              onChange={(e) => update("port", e.target.value)}
              placeholder="587"
              required
            />
          </Field>
        </div>

        <Field label="Encryption">
          <Select value={form.secure ? "ssl" : "starttls"} onChange={(e) => update("secure", e.target.value === "ssl")}>
            <option value="starttls">STARTTLS / None (port 587 / 25)</option>
            <option value="ssl">SSL / TLS (port 465)</option>
          </Select>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="SMTP Username">
            <TextInput
              value={form.username}
              onChange={(e) => update("username", e.target.value)}
              placeholder="user@example.com"
              autoComplete="off"
            />
          </Field>
          <Field label="SMTP Password" hint="Leave blank to keep existing password">
            <TextInput
              type="password"
              value={form.password}
              onChange={(e) => update("password", e.target.value)}
              placeholder="********"
              autoComplete="new-password"
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="From Email" required>
            <TextInput
              type="email"
              value={form.from_email}
              onChange={(e) => update("from_email", e.target.value)}
              placeholder="noreply@ngxptech.com"
              required
            />
          </Field>
          <Field label="From Name">
            <TextInput
              value={form.from_name}
              onChange={(e) => update("from_name", e.target.value)}
              placeholder="NGXP Technologies"
            />
          </Field>
        </div>

        <Field label="Notification Recipient" hint="Where contact-form inquiries are emailed">
          <TextInput
            type="email"
            value={form.to_email}
            onChange={(e) => update("to_email", e.target.value)}
            placeholder="info@ngxptech.com"
          />
        </Field>

        <label className="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={!!form.enabled}
            onChange={(e) => update("enabled", e.target.checked)}
            className="w-4 h-4 accent-[#7B2CBF]"
          />
          <span className="text-sm text-white">
            Enable email notifications on new contact submissions
          </span>
        </label>

        <div className="flex flex-wrap gap-3 pt-2 border-t border-white/5">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-gradient-to-r from-[#00A8E8] to-[#7B2CBF] text-white hover:opacity-90 disabled:opacity-50 transition-opacity"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </form>

      {/* Test send */}
      <div className="mt-6 bg-[#0D0E13] border border-white/5 rounded-2xl p-6">
        <h2 className="text-white font-semibold mb-1">Send Test Email</h2>
        <p className="text-gray-500 text-xs mb-4">
          Save your settings first. The test uses the currently saved configuration.
        </p>
        <div className="flex flex-col sm:flex-row gap-3">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder={form.to_email || form.from_email || "test@example.com"}
            className="flex-1 bg-[#0A0B10] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-[#7B2CBF] focus:ring-1 focus:ring-[#7B2CBF] transition-colors"
          />
          <button
            type="button"
            onClick={sendTest}
            disabled={testing}
            className="inline-flex items-center justify-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold bg-[#7B2CBF]/15 text-[#7B2CBF] border border-[#7B2CBF]/30 hover:bg-[#7B2CBF]/25 disabled:opacity-50 transition-colors"
          >
            {testing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            {testing ? "Sending..." : "Send Test"}
          </button>
        </div>
      </div>
    </div>
  );
}
